/**
 * Minimal Socket.IO v5 (over Engine.IO v4) client implemented on top of a
 * raw WebSocket. Built because socket.io-client v4.6.x and v4.8.x in the
 * production Vite bundle were calling `_sendConnectPacket` (the auth fn was
 * verifiably running) but never putting the namespace `40` packet on the
 * wire — confirmed via DevTools across multiple configurations and a
 * client-version downgrade.
 *
 * We only reconstruct the bits of the protocol the app actually uses:
 *  - OPEN handshake (engine.io `0{...}`)
 *  - Namespace CONNECT with auth (Socket.IO `40{token:...}`)
 *  - PING/PONG (`2` ↔ `3`)
 *  - Events both ways (`42[event, ...args]`)
 *  - Server DISCONNECT (`41`) + CONNECT_ERROR (`44`)
 *  - Reconnection with exponential backoff
 *
 * Default namespace `/` only. If we ever need other namespaces, extend the
 * sender to include `/ns,` between the type code and the JSON payload.
 *
 * The exposed API is intentionally a structural subset of socket.io-client's
 * Socket — `.connected`, `.id`, `.on`, `.off`, `.emit`, `.connect`,
 * `.disconnect` — so existing call sites in hooks.ts / authStore.ts don't
 * change.
 */

type Listener = (...args: unknown[]) => void;

export interface RawSocketAuth {
  token: string;
}

export interface RawSocketOptions {
  /** http(s) or ws(s) URL of the API host (without /socket.io/). */
  url: string;
  /** Called every time a connect packet is built. */
  auth: () => RawSocketAuth;
  /** Initial reconnect delay (ms). Doubles up to `reconnectionDelayMax`. */
  reconnectionDelay?: number;
  reconnectionDelayMax?: number;
}

export class RawSocket {
  public connected = false;
  public id: string | undefined;

  private opts: Required<RawSocketOptions>;
  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingWatchdog: ReturnType<typeof setTimeout> | null = null;
  private pingInterval = 25000;
  private pingTimeout = 20000;
  private explicitlyClosed = false;
  /** True once we've received OPEN — gates outbound 40/42 sends. */
  private engineOpen = false;
  /** Outgoing event packets queued while not yet connected; flushed on connect. */
  private sendBuffer: string[] = [];

  constructor(opts: RawSocketOptions) {
    this.opts = {
      url: opts.url,
      auth: opts.auth,
      reconnectionDelay: opts.reconnectionDelay ?? 1000,
      reconnectionDelayMax: opts.reconnectionDelayMax ?? 10000,
    };
  }

  connect(): this {
    this.explicitlyClosed = false;
    this._open();
    return this;
  }

  disconnect(): this {
    this.explicitlyClosed = true;
    this._clearReconnect();
    this._clearWatchdog();
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send('41'); // best-effort DISCONNECT
      } catch {
        /* ignore */
      }
    }
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
    this.ws = null;
    this.engineOpen = false;
    this.connected = false;
    this.id = undefined;
    return this;
  }

  on(event: string, handler: Listener): this {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler);
    return this;
  }

  off(event: string, handler?: Listener): this {
    if (!handler) {
      this.listeners.delete(event);
    } else {
      this.listeners.get(event)?.delete(handler);
    }
    return this;
  }

  emit(event: string, ...args: unknown[]): this {
    const data = '42' + JSON.stringify([event, ...args]);
    if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      // Buffer until the namespace is connected. socket.io-client does the
      // same internally — emits before connect aren't lost, they just wait.
      this.sendBuffer.push(data);
    }
    return this;
  }

  private _flushBuffer(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    while (this.sendBuffer.length > 0) {
      const data = this.sendBuffer.shift()!;
      this.ws.send(data);
    }
  }

  // --- internals ---

  private _toWsBase(): string {
    let u = this.opts.url;
    if (u.startsWith('https://')) u = 'wss://' + u.slice('https://'.length);
    else if (u.startsWith('http://')) u = 'ws://' + u.slice('http://'.length);
    return u.replace(/\/$/, '');
  }

  private _open(): void {
    this._clearReconnect();
    const wsUrl = this._toWsBase() + '/socket.io/?EIO=4&transport=websocket';
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      this._emitLocal('connect_error', e instanceof Error ? e : new Error(String(e)));
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.addEventListener('open', () => this._onWsOpen());
    ws.addEventListener('message', (ev) => this._onWsMessage(ev.data));
    ws.addEventListener('error', () => {
      // Don't transition state here; the close event will follow.
    });
    ws.addEventListener('close', (ev) => this._onWsClose(ev));
  }

  private _onWsOpen(): void {
    // Wait for engine.io OPEN ('0{...}') before sending 40.
  }

  private _onWsMessage(raw: unknown): void {
    if (typeof raw !== 'string' || raw.length === 0) return;

    const code = raw.charCodeAt(0);
    // Engine.IO PING (server keep-alive): respond with PONG immediately.
    if (raw === '2') {
      this._resetWatchdog();
      this.ws?.send('3');
      return;
    }
    // Engine.IO PONG ack — we don't initiate pings client-side, ignore.
    if (raw === '3') return;

    // Engine.IO OPEN: '0{...}' — handshake including pingInterval/pingTimeout.
    if (code === 48 /* '0' */) {
      try {
        const handshake = JSON.parse(raw.slice(1));
        if (typeof handshake.pingInterval === 'number') this.pingInterval = handshake.pingInterval;
        if (typeof handshake.pingTimeout === 'number') this.pingTimeout = handshake.pingTimeout;
      } catch {
        /* malformed handshake; let the close handler retry */
      }
      this.engineOpen = true;
      this._resetWatchdog();
      // Send Socket.IO CONNECT with auth.
      let authPayload: RawSocketAuth;
      try {
        authPayload = this.opts.auth();
      } catch {
        authPayload = { token: '' };
      }
      const connect = '40' + JSON.stringify(authPayload);
      this.ws?.send(connect);
      return;
    }

    // Engine.IO MESSAGE wrapping Socket.IO packet: starts with '4'.
    if (code !== 52 /* '4' */) return;

    const sioType = raw.charCodeAt(1);
    // Default namespace only; if there's a namespace header it would look
    // like '40/widget,{...}' — we only handle '/'.
    if (sioType === 48 /* '0' = CONNECT ack */) {
      let parsed: { sid?: string } = {};
      try {
        parsed = raw.length > 2 ? JSON.parse(raw.slice(2)) : {};
      } catch {
        /* ignore */
      }
      this.id = parsed.sid;
      this.connected = true;
      this.reconnectAttempt = 0;
      // Flush queued emits BEFORE notifying listeners so that any inline
      // subscribe()/emit() done by user-registered 'connect' handlers go
      // out in order after the buffered ones.
      this._flushBuffer();
      this._emitLocal('connect');
    } else if (sioType === 49 /* '1' = DISCONNECT */) {
      this.connected = false;
      this.id = undefined;
      this._emitLocal('disconnect', 'io server disconnect');
    } else if (sioType === 50 /* '2' = EVENT */) {
      // 42[event,...args]
      try {
        const arr = JSON.parse(raw.slice(2));
        if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'string') {
          const [evt, ...args] = arr as [string, ...unknown[]];
          this._emitLocal(evt, ...args);
        }
      } catch {
        /* malformed; ignore */
      }
    } else if (sioType === 52 /* '4' = CONNECT_ERROR */) {
      let msg = 'connect_error';
      try {
        const parsed = JSON.parse(raw.slice(2));
        if (typeof parsed?.message === 'string') msg = parsed.message;
      } catch {
        /* ignore */
      }
      this._emitLocal('connect_error', new Error(msg));
    }
  }

  private _onWsClose(ev: CloseEvent): void {
    const wasConnected = this.connected;
    this.connected = false;
    this.id = undefined;
    this.engineOpen = false;
    this._clearWatchdog();
    if (wasConnected) {
      this._emitLocal('disconnect', ev.reason || 'transport close');
    }
    if (!this.explicitlyClosed) {
      this._scheduleReconnect();
    }
  }

  private _scheduleReconnect(): void {
    this._clearReconnect();
    const base = this.opts.reconnectionDelay;
    const max = this.opts.reconnectionDelayMax;
    const delay = Math.min(base * Math.pow(1.5, this.reconnectAttempt), max);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this._emitLocal('reconnect_attempt', this.reconnectAttempt);
      this._open();
    }, delay);
  }

  private _clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private _resetWatchdog(): void {
    this._clearWatchdog();
    // If we go > pingInterval + pingTimeout without a server PING the
    // connection is dead — force close so onclose triggers reconnect.
    this.pingWatchdog = setTimeout(() => {
      try {
        this.ws?.close();
      } catch {
        /* ignore */
      }
    }, this.pingInterval + this.pingTimeout);
  }

  private _clearWatchdog(): void {
    if (this.pingWatchdog) {
      clearTimeout(this.pingWatchdog);
      this.pingWatchdog = null;
    }
  }

  private _emitLocal(event: string, ...args: unknown[]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const fn of Array.from(set)) {
      try {
        fn(...args);
      } catch (err) {
        console.error('[raw-socket] listener error for', event, err);
      }
    }
  }
}

// Type alias matching the relevant subset of socket.io-client's Socket so
// existing imports of `Socket` from socket.io-client can be redirected at
// the import site without further changes.
export type Socket = RawSocket;
